import Sow from "@/types/Sow";
import Badge from "@/components/atoms/Badge";
import { Paperclip } from "lucide-react";
import axios from 'axios';
import Attachment from "@/types/Attachment";
import { formatNumber } from "@/lib/utils";

type Props = {
  sow: Sow | null;
};

const Detail = ({ sow }: Props) => {
  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute('content') || '' : '';
  };

  return (
    <div className="space-y-6">
      <div className="shadow p-6">
        <span className="block text-xl font-bold mb-4">Approval Details</span>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <span className="text-sm font-light block">Admin Approval</span>
            <Badge
              variant={sow?.admin_approval ? "success" : "destructive"}
            >
              {sow?.admin_approval ? "Approved" : "Not Approved"}
            </Badge>
          </div>
          <div>
            <span className="text-sm font-light block">Internal Approval</span>
            <Badge
              variant={sow?.internal_approval ? "success" : "destructive"}
            >
              {sow?.internal_approval
                ? sow?.internal_approval_person
                  ? `Approved by ${sow?.internal_approval_person}`
                  : `Approved`
                : sow?.internal_approval_person
                ? `Waiting approval from ${sow?.internal_approval_person}`
                : `Not Approved`}
            </Badge>
          </div>
          <div>
            <span className="text-sm font-light block">External Approval</span>
            <Badge
              variant={sow?.external_approval ? "success" : "destructive"}
            >
              {sow?.external_approval
                ? sow?.external_approval_person
                  ? `Approved by ${sow?.external_approval_person}`
                  : `Approved`
                : sow?.external_approval_person
                ? `Waiting approval from ${sow?.external_approval_person}`
                : `Not Approved`}
            </Badge>
          </div>
          <div>
            <span className="text-sm font-light block">Created By</span>
            <span className="font-bold block">{sow?.created_by.name}</span>
          </div>
        </div>
      </div>

      <div className="shadow p-6">
        <span className="block text-xl font-bold mb-4">SOW Details</span>
        <div className="grid grid-cols-6 gap-12">
          <div>
            <span className="text-sm font-light block">ID</span>
            <span className="font-bold block">{sow?.display_id}</span>
          </div>
          <div className="col-span-2">
            <span className="text-sm font-light block">Name</span>
            <span className="font-bold block">{sow?.name}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Start Date</span>
            <span className="font-bold block">{sow?.start_date}</span>
          </div>
          <div>
            <span className="text-sm font-light block">End Date</span>
            <span className="font-bold block">{sow?.end_date}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Details</span>
            <span className="font-bold block">{sow?.details}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Client</span>
            <span className="font-bold block">{sow?.account.name}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Functions</span>
            <span className="font-bold block">
              {sow?.functions?.map((func) => func.name).join(", ")}
            </span>
          </div>
          <div>
            <span className="text-sm font-light block">Departments</span>
            <span className="font-bold block">
              {sow?.departments?.map((department) => department.name).join(", ")}
            </span>
          </div>
          <div>
            <span className="text-sm font-light block">Countries</span>
            <span className="font-bold block">
              {sow?.region_id 
                ? sow?.countries?.[0]?.children?.map((child) => child.name).join(", ")
                : sow?.countries?.map((country) => country.name).join(", ")}
            </span>
          </div>
          <div>
            <span className="text-sm font-light block">Client Entity</span>
            <span className="font-bold block">{sow?.entity?.name}</span>
          </div>
          <div>
            <span className="text-sm font-light block">NMQ Entity</span>
            <span className="font-bold block">{sow?.nmq_entity?.name}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Payment System</span>
            <span className="font-bold block">{sow?.payment_system}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Payment Type</span>
            <span className="font-bold block">{sow?.payment_type}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Master SOW</span>
            <span className="font-bold block">{sow?.master_sow?.display_id}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Renewed SOW</span>
            <span className="font-bold block">{sow?.parent?.display_id}</span>
          </div>
          <div>
            <span className="text-sm font-light block">PO Number</span>
            <span className="font-bold block">{sow?.po_number}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Approved Invoiced Amount</span>
            <span className="font-bold block">{formatNumber(sow?.invoiced_total_amount || 0)}</span>
          </div>
        </div>
      </div>

      <div className="shadow p-6">
        <span className="block text-xl font-bold mb-4">Attachments</span>
        <div className="grid grid-cols-1 gap-4">
          {sow?.attachments?.map((attachment) => (
            <div key={attachment.id} className="flex">
              <Paperclip size={24} className="text-blue-500 mr-2" />
              <a
                href={attachment.url || ''}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-blue-500 hover:underline block"
              >
                {attachment.original_name || attachment.name}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Detail;
